import { Component } from '@theme/component';
import { prefersReducedMotion, debounce } from '@theme/utilities';

/**
 * An endless kinetic drag-to-scroll image gallery.
 *
 * Pointer Events drive a single mouse/trackpad/touch code path. The track is
 * moved with `transform: translate3d()` and the original image set is cloned so
 * the row loops seamlessly in both directions — the visible transform is always
 * wrapped into one set-width, and because the cloned content is identical the
 * wrap is invisible. On release, momentum keeps the row gliding under friction.
 * An optional slow auto-drift rolls the row while idle.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} viewport - The clipping viewport (focusable, captures pointers).
 * @property {HTMLElement} track - The flex row that is transformed.
 *
 * @extends {Component<Refs>}
 */
class WlDragGallery extends Component {
  requiredRefs = ['viewport', 'track'];

  /** Current logical position in px (unbounded; wrapped only at render time). */
  #position = 0;
  /** Current velocity in px per millisecond. */
  #velocity = 0;
  /** Width of one original image set (the loop period), in px. */
  #setWidth = 0;
  /** Per-16.6ms decay factor applied to velocity above the target. */
  #friction = 0.94;
  /** Whether auto-drift is enabled. */
  #driftEnabled = false;
  /** Constant drift velocity in px/ms (signed). */
  #driftVelocity = 0;
  /** True once reduced-motion fallback is active (no JS motion). */
  #reduced = false;

  /** @type {Element[]} The original (non-clone) items. */
  #originalItems = [];
  /** @type {Element[]} Appended clone items. */
  #clones = [];

  #raf = null;
  #lastTime = 0;
  #dragging = false;
  #pointerId = null;
  #lastX = 0;
  #lastMoveTime = 0;
  #moved = 0;

  /** Below this speed (px/ms) the roll is considered stopped. */
  #STOP = 0.004;
  /** Drag distance (px) past which a trailing click is suppressed. */
  #CLICK_THRESHOLD = 6;

  connectedCallback() {
    super.connectedCallback();

    const { track } = this.refs;
    if (!track) return;

    this.#originalItems = Array.from(track.children);
    if (this.#originalItems.length === 0) return;

    this.#readConfig();

    // Reduced motion: leave the native scroll fallback (CSS sets overflow-x:auto),
    // do not clone, transform, or run any animation loop.
    if (prefersReducedMotion()) {
      this.#reduced = true;
      return;
    }

    // Suppress the native image drag-ghost across the whole track.
    track.addEventListener('dragstart', this.#preventDefault);

    this.refs.viewport.addEventListener('pointerdown', this.#onPointerDown);
    this.refs.viewport.addEventListener('pointermove', this.#onPointerMove);
    this.refs.viewport.addEventListener('pointerup', this.#onPointerUp);
    this.refs.viewport.addEventListener('pointercancel', this.#onPointerUp);
    this.refs.viewport.addEventListener('lostpointercapture', this.#onPointerUp);
    this.refs.viewport.addEventListener('keydown', this.#onKeyDown);

    this.#resizeObserver = new ResizeObserver(this.#handleResize);
    this.#resizeObserver.observe(this.refs.viewport);

    // Re-measure once images report their real dimensions.
    for (const img of track.querySelectorAll('img')) {
      if (!(/** @type {HTMLImageElement} */ (img).complete)) {
        img.addEventListener('load', this.#handleResize, { once: true });
      }
    }

    // Wait one frame so layout (and any width/height attributes) settle.
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.#build();
      if (this.#driftEnabled) this.#start();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
    this.#resizeObserver?.disconnect();

    const { viewport, track } = this.refs;
    track?.removeEventListener('dragstart', this.#preventDefault);
    viewport?.removeEventListener('pointerdown', this.#onPointerDown);
    viewport?.removeEventListener('pointermove', this.#onPointerMove);
    viewport?.removeEventListener('pointerup', this.#onPointerUp);
    viewport?.removeEventListener('pointercancel', this.#onPointerUp);
    viewport?.removeEventListener('lostpointercapture', this.#onPointerUp);
    viewport?.removeEventListener('keydown', this.#onKeyDown);
  }

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  #readConfig() {
    const momentum = clamp(Number(this.dataset.momentum ?? 70), 0, 100);
    // Map 0–100 → per-frame decay 0.85 (snappy stop) … 0.975 (long glide).
    this.#friction = 0.85 + (momentum / 100) * 0.125;

    this.#driftEnabled = this.dataset.drift === 'true';
    const speed = Math.max(0, Number(this.dataset.driftSpeed ?? 30)) / 1000; // px/ms
    const sign = this.dataset.driftDirection === 'right' ? 1 : -1;
    this.#driftVelocity = this.#driftEnabled ? sign * speed : 0;
  }

  /** Measure the set width and (re)create just enough clones to fill the viewport. */
  #build() {
    this.#measure();
    this.#buildClones();
    this.#render();
  }

  #measure() {
    let width = 0;
    const gap = parseFloat(getComputedStyle(this.refs.track).columnGap) || 0;
    for (const item of this.#originalItems) {
      width += /** @type {HTMLElement} */ (item).offsetWidth;
    }
    // Each item carries a trailing margin-inline-end (the gap), so the loop
    // period is the summed item widths plus one gap per item.
    const marginGap = parseFloat(
      getComputedStyle(/** @type {HTMLElement} */ (this.#originalItems[0])).marginInlineEnd
    ) || gap;
    width += marginGap * this.#originalItems.length;
    this.#setWidth = width;
  }

  #buildClones() {
    for (const clone of this.#clones) clone.remove();
    this.#clones = [];

    if (this.#setWidth <= 0) return;

    const viewportWidth = this.refs.viewport.offsetWidth;
    // Total content must exceed viewport + 2×setWidth so a wrapped transform in
    // (-setWidth, 0] always leaves content covering the full viewport.
    const copies = Math.max(2, Math.ceil((viewportWidth + 2 * this.#setWidth) / this.#setWidth));

    const fragment = document.createDocumentFragment();
    for (let copy = 1; copy < copies; copy++) {
      for (const item of this.#originalItems) {
        const clone = /** @type {HTMLElement} */ (item.cloneNode(true));
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('inert', '');
        clone.setAttribute('tabindex', '-1');
        this.#clones.push(clone);
        fragment.appendChild(clone);
      }
    }
    this.refs.track.appendChild(fragment);
  }

  /** Apply the wrapped transform. The visible offset stays within (-setWidth, 0]. */
  #render() {
    if (this.#setWidth <= 0) {
      this.refs.track.style.transform = 'translate3d(0,0,0)';
      return;
    }
    let x = this.#position % this.#setWidth;
    if (x > 0) x -= this.#setWidth;
    this.refs.track.style.transform = `translate3d(${x}px, 0, 0)`;
  }

  #handleResize = debounce(() => {
    if (!this.isConnected || this.#reduced) return;
    this.#build();
  }, 200);

  // --- Animation (momentum + drift) ---

  #start() {
    if (this.#raf != null) return;
    this.#lastTime = performance.now();
    this.#raf = requestAnimationFrame(this.#frame);
  }

  #stop() {
    if (this.#raf != null) cancelAnimationFrame(this.#raf);
    this.#raf = null;
  }

  #frame = (now) => {
    const dt = Math.min(now - this.#lastTime, 64) || 16.667;
    this.#lastTime = now;

    const target = this.#driftEnabled ? this.#driftVelocity : 0;
    // Decay only the velocity in excess of the drift target, so momentum eases
    // into a steady drift (or to a stop when drift is off).
    this.#velocity = target + (this.#velocity - target) * Math.pow(this.#friction, dt / 16.667);
    if (Math.abs(this.#velocity - target) < this.#STOP) this.#velocity = target;

    this.#position += this.#velocity * dt;
    this.#render();

    if (this.#velocity !== 0 || target !== 0) {
      this.#raf = requestAnimationFrame(this.#frame);
    } else {
      this.#raf = null;
    }
  };

  // --- Pointer drag ---

  #onPointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    this.#dragging = true;
    this.#pointerId = event.pointerId;
    this.#lastX = event.clientX;
    this.#lastMoveTime = performance.now();
    this.#velocity = 0;
    this.#moved = 0;

    this.#stop();
    this.setAttribute('dragging', '');
    try {
      this.refs.viewport.setPointerCapture(event.pointerId);
    } catch {
      /* capture may fail on synthetic events; pointer listeners still fire */
    }
  };

  #onPointerMove = (event) => {
    if (!this.#dragging || event.pointerId !== this.#pointerId) return;

    const dx = event.clientX - this.#lastX;
    this.#lastX = event.clientX;
    this.#moved += Math.abs(dx);
    this.#position += dx;

    const now = performance.now();
    const elapsed = now - this.#lastMoveTime;
    if (elapsed > 0) {
      // Light smoothing keeps the release velocity stable against jitter.
      this.#velocity = this.#velocity * 0.7 + (dx / elapsed) * 0.3;
      this.#lastMoveTime = now;
    }

    this.#render();
  };

  #onPointerUp = (event) => {
    if (!this.#dragging || event.pointerId !== this.#pointerId) return;

    this.#dragging = false;
    this.#pointerId = null;
    this.removeAttribute('dragging');
    try {
      this.refs.viewport.releasePointerCapture(event.pointerId);
    } catch {
      /* already released */
    }

    // If the pointer stalled before release, treat it as no flick.
    if (performance.now() - this.#lastMoveTime > 80) this.#velocity = 0;

    // Suppress the click that follows a real drag so links/lightboxes don't fire.
    if (this.#moved > this.#CLICK_THRESHOLD) {
      this.refs.track.addEventListener('click', this.#preventClick, { capture: true });
      setTimeout(() => {
        this.refs.track.removeEventListener('click', this.#preventClick, { capture: true });
      }, 0);
    }

    // Roll on (momentum), then ease into drift if enabled. Always start so the
    // loop can settle the row even from a standstill when drift is on.
    if (Math.abs(this.#velocity) > this.#STOP || this.#driftEnabled) this.#start();
  };

  #onKeyDown = (event) => {
    if (this.#reduced) return;
    let nudge = 0;
    if (event.key === 'ArrowRight') nudge = -0.6; // reveal items to the right
    else if (event.key === 'ArrowLeft') nudge = 0.6;
    else return;

    event.preventDefault();
    this.#velocity = nudge; // px/ms — friction turns the nudge into a short glide
    this.#start();
  };

  #preventClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  #preventDefault = (event) => event.preventDefault();
}

/**
 * Clamp a number to a range.
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

if (!customElements.get('wl-drag-gallery')) {
  customElements.define('wl-drag-gallery', WlDragGallery);
}
